import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SCOPE_LABELS, type ConsentInfo } from '@authsemply/shared';
import { AuthCard } from '../components/AuthCard';
import { Alert } from '../components/Alert';
import { api, ApiError } from '../lib/api';

/**
 * Écran de consentement, affiché uniquement pour les clients tiers : les applis
 * SEmply sont first-party et l'API les laisse passer sans passer par ici.
 *
 * La requête /authorize d'origine arrive dans l'URL et est renvoyée telle quelle
 * à l'API, qui la revalide avant de construire la redirection. Le portail ne
 * fabrique aucune URL de redirection lui-même.
 */
export function ConsentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const clientId = searchParams.get('client_id');
  const scope = searchParams.get('scope');

  useEffect(() => {
    if (!clientId || !scope) {
      setError('Requête incomplète.');
      return;
    }

    api
      .get<ConsentInfo>(`/oidc/consent/info?client_id=${encodeURIComponent(clientId)}&scope=${encodeURIComponent(scope)}`)
      .then(setInfo)
      .catch((err: unknown) => {
        // Session expirée entre /authorize et cet écran : on repasse par la
        // connexion en conservant les paramètres du flux.
        if (err instanceof ApiError && err.status === 401) {
          navigate(`/login?${searchParams.toString()}`, { replace: true });
          return;
        }
        setError("Cette demande d'autorisation est invalide ou a expiré.");
      });
  }, [clientId, scope, navigate, searchParams]);

  async function decide(approved: boolean) {
    setPending(true);
    setError(null);
    try {
      const body = Object.fromEntries(searchParams.entries());
      const { location } = await api.post<{ location: string }>('/oidc/consent', {
        ...body,
        approved,
      });
      window.location.assign(location);
    } catch {
      setError("La décision n'a pas pu être enregistrée.");
      setPending(false);
    }
  }

  if (error && !info) {
    return (
      <AuthCard title="Autorisation impossible">
        <Alert message={error} />
      </AuthCard>
    );
  }

  if (!info) {
    return <AuthCard title="Chargement…">{null}</AuthCard>;
  }

  const newScopes = info.requestedScopes.filter((scope) => !info.grantedScopes.includes(scope));

  return (
    <AuthCard
      title="Autoriser l'accès"
      subtitle={`${info.client.name} demande l'accès à votre compte SEmply.`}
    >
      <Alert message={error} />

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', fontSize: '0.9rem' }}>
        {info.requestedScopes.map((scope) => {
          const label = SCOPE_LABELS[scope];
          return (
            <li key={scope} style={{ padding: '0.6rem 0', borderBottom: '1px solid var(--color-border)' }}>
              <strong>{label?.title ?? scope}</strong>
              {label?.description ? (
                <div style={{ color: 'var(--text-color-light)', fontSize: '0.85rem' }}>{label.description}</div>
              ) : null}
              {!newScopes.includes(scope) ? (
                <div style={{ color: 'var(--color-success)', fontSize: '0.8rem' }}>Déjà autorisé</div>
              ) : null}
            </li>
          );
        })}
      </ul>

      <button className="btn" type="button" disabled={pending} onClick={() => void decide(true)}>
        {pending ? 'Enregistrement…' : 'Autoriser'}
      </button>
      <button
        className="btn btn-secondary"
        style={{ marginTop: '0.5rem' }}
        type="button"
        disabled={pending}
        onClick={() => void decide(false)}
      >
        Refuser
      </button>

      <p className="subtitle" style={{ marginTop: '1.25rem', marginBottom: 0 }}>
        Vous pourrez retirer cet accès à tout moment depuis votre compte.
      </p>
    </AuthCard>
  );
}
